import { useState, useRef, useEffect, type FormEvent } from 'react';
import { useRouter } from '@/domains/shared/router/Router';
import { useDiagnostic } from '@/contexts/DiagnosticContext';
import { TURNSTILE_SITE_KEY } from '@/domains/assessment/constants';
import { ConsentCheckbox } from '@/domains/shared/components/ConsentCheckbox';
import { Button } from '@/domains/shared/components/Button';
import { Card } from '@/domains/shared/components/Card';

// ==============================================================================
// TEMPORARY EVENT MVP MODE (Ativo para o evento presencial)
// ==============================================================================
// - Widget do Turnstile oculto temporariamente para participantes do evento
// - Honeypot invisível ativo para bloqueio automatizado de spam
// ==============================================================================
const EVENT_MVP_MODE = true;

export function ConsentPage() {
  const { navigate } = useRouter();
  const { consents, setConsents, turnstileToken, setTurnstileToken, honeypot, setHoneypot } = useDiagnostic();

  const [privacy, setPrivacy] = useState(consents.consentPrivacy);
  const [contact, setContact] = useState(consents.consentContact);
  const [error, setError] = useState<string | null>(null);
  const [turnstileError, setTurnstileError] = useState<string | null>(null);

  const turnstileContainerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);

  const isProduction = import.meta.env.VITE_APP_ENV === 'production';

  useEffect(() => {
    // Em EVENT_MVP_MODE, o widget não é renderizado
    if (EVENT_MVP_MODE) {
      setTurnstileToken('event_mvp_mode_token');
      return;
    }

    // 1. Limpa o token ao montar para garantir que não haja reutilização
    setTurnstileToken('');
    setTurnstileError(null);

    // 2. Remove/reset de qualquer widget anterior antes de renderizar nova instância
    if (typeof window !== 'undefined' && window.turnstile) {
      if (widgetIdRef.current) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          // ignore cleanup errors
        }
        widgetIdRef.current = null;
      }
    }
    if (turnstileContainerRef.current) {
      turnstileContainerRef.current.innerHTML = '';
    }

    let intervalId: ReturnType<typeof setInterval> | null = null;

    const renderWidget = () => {
      if (
        typeof window !== 'undefined' &&
        window.turnstile &&
        turnstileContainerRef.current &&
        !widgetIdRef.current
      ) {
        try {
          const id = window.turnstile.render(turnstileContainerRef.current, {
            sitekey: TURNSTILE_SITE_KEY,
            theme: 'dark',
            callback: (token: string) => {
              setTurnstileToken(token);
              setTurnstileError(null);
            },
            'error-callback': () => {
              setTurnstileToken('');
              setTurnstileError('Erro na verificação de segurança. Por favor, tente novamente.');
              if (widgetIdRef.current && window.turnstile?.reset) {
                try {
                  window.turnstile.reset(widgetIdRef.current);
                } catch {
                  // ignore reset errors
                }
              }
            },
            'expired-callback': () => {
              setTurnstileToken('');
              setTurnstileError('A verificação expirou. Por favor, marque novamente.');
              if (widgetIdRef.current && window.turnstile?.reset) {
                try {
                  window.turnstile.reset(widgetIdRef.current);
                } catch {
                  // ignore reset errors
                }
              }
            },
          });
          widgetIdRef.current = id;
          if (intervalId) {
            clearInterval(intervalId);
            intervalId = null;
          }
        } catch (err) {
          console.error('Erro ao renderizar widget Turnstile:', err);
        }
      }
    };

    if (typeof window !== 'undefined') {
      if (window.turnstile) {
        renderWidget();
      } else {
        intervalId = setInterval(() => {
          if (window.turnstile) {
            renderWidget();
          }
        }, 200);
      }
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [setTurnstileToken]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    if (!privacy) {
      setError('É obrigatório concordar com os termos de privacidade para gerar o diagnóstico.');
      return;
    }

    if (!EVENT_MVP_MODE && isProduction && (!turnstileToken || turnstileToken === '1x00000000000000000000AA')) {
      setTurnstileError('Por favor, aguarde e conclua a verificação anti-bot Cloudflare Turnstile antes de prosseguir.');
      return;
    }

    setConsents({
      consentPrivacy: privacy,
      consentContact: contact,
    });

    navigate('/processando');
  };

  return (
    <div className="flex min-h-screen flex-col bg-navy text-brand-offWhite">
      <header className="border-b border-white/10 bg-navy-dark/90 px-4 py-3.5 sticky top-0 z-40">
        <div className="mx-auto flex max-w-2xl items-center justify-between">
          <button
            type="button"
            onClick={() => navigate('/identificacao')}
            className="inline-flex min-h-[44px] items-center gap-1.5 text-xs font-semibold text-brand-offWhite/80 hover:text-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange rounded-lg px-2"
          >
            <span>←</span> Voltar aos dados de contato
          </button>
          <span className="text-xs font-bold text-brand-orange">Conformidade e Envio</span>
        </div>
      </header>

      <main className="mx-auto flex w-full max-w-2xl flex-1 flex-col justify-center px-4 py-8 sm:py-12">
        <div className="text-left">
          <span className="inline-block rounded bg-brand-orange/20 px-2.5 py-1 text-xs font-bold uppercase tracking-wider text-brand-orange">
            Privacidade e Segurança
          </span>
          <h1 className="mt-3 text-2xl font-black text-white sm:text-3xl">
            Termos e Autorização de Envio
          </h1>
          <p className="mt-2 text-sm text-brand-offWhite/80">
            Respeitamos sua privacidade e a LGPD. Mantenha controle sobre como seus dados são utilizados.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="mt-8 space-y-4 text-left">
          {/* Honeypot invisível anti-spam (EVENT_MVP_MODE) */}
          <div
            style={{
              position: 'absolute',
              width: '1px',
              height: '1px',
              padding: '0',
              margin: '-1px',
              overflow: 'hidden',
              clip: 'rect(0, 0, 0, 0)',
              whiteSpace: 'nowrap',
              border: '0',
            }}
            aria-hidden="true"
          >
            <label htmlFor="website_url">Não preencha este campo</label>
            <input
              id="website_url"
              type="text"
              name="website_url"
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
              tabIndex={-1}
              autoComplete="off"
            />
          </div>

          {/* Consentimento 1: LGPD (Obrigatório) */}
          <ConsentCheckbox
            id="consent-privacy"
            checked={privacy}
            onChange={(e) => {
              setPrivacy(e.target.checked);
              if (e.target.checked) setError(null);
            }}
            error={error ?? undefined}
            label={
              <span>
                Concordo com os{' '}
                <span className="font-semibold text-brand-orange underline">
                  Termos de Uso e Política de Privacidade
                </span>{' '}
                para processamento e cálculo do diagnóstico estratégico da minha empresa contábil.
              </span>
            }
          />

          {/* Consentimento 2: Contato Comercial (Separado) */}
          <ConsentCheckbox
            id="consent-contact"
            checked={contact}
            onChange={(e) => setContact(e.target.checked)}
            label={
              <span>
                Aceito receber orientações complementares e contato direto da equipe de{' '}
                <strong className="text-white">Israel Costa e DecolaCont</strong> via WhatsApp e
                e-mail sobre o meu diagnóstico.
              </span>
            }
          />

          {/* Widget Real Cloudflare Turnstile (Oculto em EVENT_MVP_MODE) */}
          {!EVENT_MVP_MODE && (
            <Card
              className="p-4 bg-white/5 border-white/10 flex flex-col items-center justify-center gap-3 mt-6"
              data-turnstile-sitekey={TURNSTILE_SITE_KEY}
            >
              <div className="w-full flex items-center justify-between border-b border-white/10 pb-3">
                <div className="flex items-center gap-2.5">
                  <div className={`flex h-6 w-6 items-center justify-center rounded-full ${turnstileToken ? 'bg-feedback-success/20 text-feedback-success' : 'bg-brand-orange/20 text-brand-orange'}`}>
                    {turnstileToken ? (
                      <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    ) : (
                      <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
                      </svg>
                    )}
                  </div>
                  <div className="text-left">
                    <span className="block text-xs font-semibold text-white">
                      Verificação de Segurança Anti-Bot
                    </span>
                    <span className="block text-[11px] text-brand-offWhite/60">
                      Proteção Cloudflare Turnstile ativa sem necessidade de captchas invasivos.
                    </span>
                  </div>
                </div>
                <span className={`text-[10px] uppercase font-bold tracking-wider ${turnstileToken ? 'text-feedback-success' : 'text-brand-orange'}`}>
                  {turnstileToken ? 'Verificado' : 'Validando...'}
                </span>
              </div>

              {/* Container onde o widget oficial da Cloudflare se renderiza */}
              <div
                ref={turnstileContainerRef}
                id="cf-turnstile-widget"
                className="min-h-[65px] flex items-center justify-center my-1"
              />

              {turnstileError && (
                <p className="text-xs text-brand-orange font-medium mt-1">
                  {turnstileError}
                </p>
              )}
            </Card>
          )}

          <div className="mt-8 pt-4">
            <Button
              type="submit"
              variant="primary"
              size="lg"
              disabled={!privacy || (!EVENT_MVP_MODE && isProduction && !turnstileToken)}
              className="w-full text-base font-bold shadow-xl shadow-brand-orange/20 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Gerar Meu Diagnóstico Agora →
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
}

